import _ from 'lodash'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Box, Button, Card, CardActions, CardContent, CardHeader, Checkbox, CircularProgress, Grid, IconButton, Stack, Tooltip } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import QrCode2RoundedIcon from '@mui/icons-material/QrCode2Rounded'

import { useCreateUploadGrantMutation } from '@/core/services/file'

import DeleteConfirmationDialog from '@/components/ui/dialogs/DeleteConfirmationDialog'
import Document from '@/components/group/Document'
import Tabs from '@/components/ui/tabs/Tabs'
import TabPanel from '@/components/ui/tab/TabPanel'
import TextInput from '@/components/ui/inputs/TextInput'
import UploadButton from '@/components/ui/buttons/UploadButton'

import { getApiErrorMessage } from '@/utils/errors'

import QrCodeDialog from './QrCodeDialog'

export type BiologicalProps = Readonly<{
  comment?: string
  files: Array<{
    filename: string
    key: string
    url: string
  }>
  id: string
  isDeletingFiles?: boolean
  label: string
  onDeleteBiological?: (biologicalId: string) => void
  onDeleteFiles?: (biologicalId: string, files: BiologicalProps['files']) => Promise<void>
  onUploadFile?: (file: File, biologicalId: string) => void
  onExtractVitalSigns?: (fileKey: string) => void
  isProcessingVitalSigns?: boolean
  processingVitalSignsFileKey?: string | null
  consultationId?: string
  readOnly?: boolean
  unit: string
  value: string
}>

export default function Biological(props: BiologicalProps) {
  const {
    comment,
    files,
    id,
    isDeletingFiles = false,
    label,
    onDeleteBiological,
    onDeleteFiles,
    onUploadFile,
    onExtractVitalSigns,
    isProcessingVitalSigns = false,
    processingVitalSignsFileKey = null,
    consultationId: consultationIdProp,
    readOnly = false,
    unit,
    value,
  } = props

  const { consultationId: consultationIdFromParams } = useParams()
  const consultationId = consultationIdProp || consultationIdFromParams
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const [createUploadGrant, { isLoading: isGeneratingMagicLink, data }] = useCreateUploadGrantMutation()
  const url = data?.url

  const [deleteConfirmationDialogOpen, setDeleteConfirmationDialogOpen] = useState(false)
  const [qrCodeDialogOpen, setQrCodeDialogOpen] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<Record<string, BiologicalProps['files'][number]>>({})

  return (
    <Card sx={{ width: '100%', minHeight: '150px', position: 'relative' }}>
      <CardHeader
        action={
          !readOnly ? (
            <Stack direction="row">
              <IconButton
                color="primary"
                onClick={async () => {
                  if (!consultationId) return
                  try {
                    await createUploadGrant({ objectType: 'consultation', objectId: consultationId, biologicalId: id }).unwrap()
                    setQrCodeDialogOpen(true)
                  } catch (error) {
                    enqueueSnackbar(getApiErrorMessage(error, t('notification.magicLink.create.error')), { variant: 'error' })
                  }
                }}
              >
                <QrCode2RoundedIcon />
                {isGeneratingMagicLink && (
                  <CircularProgress
                    disableShrink
                    size={24}
                    sx={{
                      position: 'absolute',
                      zIndex: 1,
                    }}
                  />
                )}
              </IconButton>
              <IconButton onClick={() => onDeleteBiological?.(id)}>
                <DeleteIcon color="error" />
              </IconButton>
            </Stack>
          ) : null
        }
        title={label}
        sx={{
          '& .MuiCardHeader-title': {
            fontSize: '20px',
            fontWeight: 800,
          },
        }}
      />
      <Tabs
        centered={false}
        renderTabPanel={({ value: tabPanelValue }) => (
          <>
            <TabPanel value={tabPanelValue} index={0}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextInput fullWidth label={t('field.value.label')} name="value" value={value} readOnly />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextInput fullWidth label={t('field.unit.label')} name="unit" value={unit} readOnly />
                  </Grid>
                  <Grid item xs={12}>
                    <TextInput
                      fullWidth
                      label={t('field.comment.label')}
                      name="comment"
                      multiline
                      rows={4}
                      placeholder={t('component.group.biologicals.noComment')}
                      value={comment}
                      readOnly
                    />
                  </Grid>
                  <Grid item></Grid>
                </Grid>
              </CardContent>
            </TabPanel>
            <TabPanel value={tabPanelValue} index={1}>
              <CardContent sx={{ pb: 0 }}>
                <Grid container spacing={3}>
                  {_.map(files, (file, index) => (
                    <Grid key={index} item xs={12} sm={4} md={3} xl={2} alignItems="center">
                      <Box sx={{ position: 'relative' }}>
                        <Document
                          disableAnnotation
                          disableChangeName
                          extension={_.split(file.filename, '.')[1]}
                          fileKey={file.key}
                          name={_.split(file.filename, '.')[0]}
                          src={file.url}
                        />
                        {!readOnly && (
                          <Checkbox
                            checked={!!selectedFiles[file.key]}
                            size="small"
                            sx={{ position: 'absolute', top: '0', right: '0' }}
                            onChange={(_event, checked) => {
                              setSelectedFiles((prevSelectedFiles) => {
                                return !checked ? _.omit(prevSelectedFiles, file.key) : { ...prevSelectedFiles, [file.key]: file }
                              })
                            }}
                          />
                        )}
                      </Box>
                    </Grid>
                  ))}
                  {!readOnly && (
                    <Grid item xs={12} sm={4} md={3} xl={2} alignItems="center">
                      <UploadButton onUpload={(file) => onUploadFile?.(file, id)} />
                    </Grid>
                  )}
                </Grid>
              </CardContent>
              {!!Object.keys(selectedFiles).length && (
                <CardActions sx={{ justifyContent: 'end', px: 3, pb: 3 }}>
                  <Button color="error" onClick={() => setDeleteConfirmationDialogOpen(true)} variant="contained">
                    {t('common:button.delete.label')}
                  </Button>
                </CardActions>
              )}
            </TabPanel>
          </>
        )}
        tabs={[
          { label: t('component.diagnostic.biological.infoTab.label'), id: 'biological-info' },
          { label: t('component.diagnostic.biological.documentsTab.label'), id: 'biological-documents' },
        ]}
        variant="fullWidth"
      />
      <DeleteConfirmationDialog
        isDeleting={isDeletingFiles}
        onClose={() => setDeleteConfirmationDialogOpen(false)}
        onDelete={async () => {
          await onDeleteFiles?.(id, _.values(selectedFiles))
          setDeleteConfirmationDialogOpen(false)
        }}
        open={deleteConfirmationDialogOpen}
        title={t('dialog.deleteConfirmation.documents.title', { count: _.keys(selectedFiles).length })}
        message={t('dialog.deleteConfirmation.documents.message')}
      />
      <QrCodeDialog onClose={() => setQrCodeDialogOpen(false)} open={qrCodeDialogOpen} qrCodeValue={url ?? ''} />
    </Card>
  )
}
